import { cloneElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Kbd } from './Kbd.tsx'
import { cn } from './util.ts'

export interface TooltipProps {
  content: ReactNode
  /** Shortcut shown in the tooltip (rendered from the shortcut registry by the shell). */
  kbd?: string[]
  side?: 'top' | 'bottom' | 'right'
  /** The trigger; it receives aria-describedby while the tooltip shows. */
  children: ReactElement<{ 'aria-describedby'?: string }>
  disabled?: boolean
}

/** Hover or focus hint: <Tooltip content="Toggle theme" kbd={['t']}><Button …/></Tooltip>. */
export function Tooltip({ content, kbd, side = 'top', children, disabled }: TooltipProps) {
  const id = useId()
  const anchor = useRef<HTMLSpanElement>(null)
  const timer = useRef<number | undefined>(undefined)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)

  const place = () => {
    const r = anchor.current?.getBoundingClientRect()
    if (!r) return
    if (side === 'right') setPos({ x: r.right + 8, y: r.top + r.height / 2 })
    else if (side === 'bottom') setPos({ x: r.left + r.width / 2, y: r.bottom + 8 })
    else setPos({ x: r.left + r.width / 2, y: r.top - 8 })
  }
  const show = (delay: number) => {
    if (disabled) return
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(place, delay)
  }
  const hide = () => {
    window.clearTimeout(timer.current)
    setPos(null)
  }

  useEffect(() => {
    if (!pos) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && hide()
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', hide, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', hide, true)
    }
  }, [pos])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  return (
    <span
      ref={anchor}
      className="inline-flex"
      onPointerEnter={(e) => e.pointerType === 'mouse' && show(450)}
      onPointerLeave={hide}
      onFocus={(e) => (e.target as HTMLElement).matches(':focus-visible') && show(0)}
      onBlur={hide}
      onPointerDown={hide}
    >
      {cloneElement(children, { 'aria-describedby': pos ? id : undefined })}
      {pos &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            style={{ left: pos.x, top: pos.y }}
            className={cn(
              'pointer-events-none fixed z-80 flex max-w-xs items-center gap-2 rounded-sm bg-overlay px-2 py-1 text-sm text-ink shadow-overlay',
              side === 'top' && '-translate-x-1/2 -translate-y-full',
              side === 'bottom' && '-translate-x-1/2',
              side === 'right' && '-translate-y-1/2',
            )}
          >
            {content}
            {kbd && <Kbd keys={kbd} tone="plain" />}
          </span>,
          document.body,
        )}
    </span>
  )
}
